import { useEffect, useId, useRef } from 'react'
import { PressScale } from '../../lib/PressScale'

/**
 * Every non-roster state, with the SAME shape: a sentence, an explanation and
 * two working ways out. No state on this surface is allowed to render without
 * one, and none of them is "sign out".
 *
 * ── `announce` — FOR THE ONES THAT REPLACE SOMETHING ──────────────────────
 * Most of these render as the whole page and need nothing. But some SWAP IN
 * over a surface the user was already using — accepting an invite replaces the
 * accept panel with its outcome — and a swap that is neither focused nor
 * announced is silent: the button vanishes, focus falls to `<body>`, and a
 * screen reader says nothing at all about whether they just joined a company.
 * `announce` puts focus on the heading (described by the body) so the outcome
 * is read once, and the exits are the very next tab stops.
 *
 * It is opt-in precisely because it is wrong on the other ones: stealing focus
 * on first paint fights the browser's own start position.
 */
export function BareState({
  title, heading, body, primary, secondary, testId, announce = false,
}: {
  title: string
  heading: string
  body: string
  primary: { label: string; onClick?: () => void; href?: string }
  secondary?: { label: string; onClick?: () => void; href?: string }
  testId?: string
  /** Focus + read the heading on mount. For states that REPLACE a surface. */
  announce?: boolean
}) {
  const headingRef = useRef<HTMLParagraphElement>(null)
  const bodyId = useId()
  useEffect(() => {
    if (!announce) return
    headingRef.current?.focus()
  }, [announce])
  const btn = (a: { label: string; onClick?: () => void; href?: string }, cls: string, tid: string) =>
    (a.href
      ? <a key={a.label} href={a.href} className={`${cls} inline-flex items-center justify-center`} data-testid={tid}>{a.label}</a>
      : <PressScale key={a.label} feel="tap" onClick={a.onClick} className={cls} data-testid={tid}>{a.label}</PressScale>)

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6" data-testid={testId}>
      <h1 className="font-serif text-2xl tracking-h1 text-ink md:text-4xl">{title}</h1>
      <div className="rule-strong mt-5" />
      <p
        ref={headingRef}
        tabIndex={announce ? -1 : undefined}
        aria-describedby={announce ? bodyId : undefined}
        className="mt-6 text-lg font-medium text-ink focus:outline-none"
      >
        {heading}
      </p>
      <p id={bodyId} className="mt-2 max-w-[46ch] text-sm leading-relaxed text-soft">{body}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {btn(primary, 'btn-primary h-11 px-5', `${testId ?? 'state'}-primary`)}
        {secondary && btn(secondary, 'btn-secondary h-11 px-5', `${testId ?? 'state'}-secondary`)}
      </div>
    </div>
  )
}
